import ChatBubbleOutline from '@mui/icons-material/ChatBubbleOutlineOutlined';
import SwapHoriz from '@mui/icons-material/SwapHoriz';
import {
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	MenuItem,
	Select,
	type SelectChangeEvent,
	Stack,
	Theme,
	Typography,
	useMediaQuery,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SystemIcon } from '@volvo/vcdk-react/SystemIcon';
import { useSnackbar } from 'notistack';
import { Fragment, useCallback, useMemo, useState } from 'react';
import type React from 'react';
import { useTranslation } from 'react-i18next';

import { getUrlFileSetDownload } from 'api/ApiUrls';
import { useProductCenterApi } from 'api/ApplicationApi';
import { unitInfoQuery, unitKeys } from 'api/queries';
import type { ImportAdditionalFilesToServiceAgreementCommand } from 'api/v1/data-contracts';
import UserIcon from 'assets/volvoIcons/People/UserIcon';
import { FileDisplayBase } from 'library/components/controls/files/FileDisplayBase';
import { getFileType } from 'library/components/controls/files/functions';
import { selectUser } from 'library/redux/application';
import { useAppSelector } from 'library/redux/hooks';
import { TransferFilesDialogActions } from 'modules/UnitOverview/Components/TransferFilesDialogActions';
import { TransferFilesStatusContent } from 'modules/UnitOverview/Components/TransferFilesStatusContent';

type FlatFileRow = {
	rowKey: string;
	fileSetId: number;
	fileId: number;
	fileName: string;
};

type FileGroup = {
	fileSetId: number;
	comment: string;
	submittedBy: string;
	isOwn: boolean;
	files: FlatFileRow[];
};

const groupGridSx = {
	display: 'grid',
	alignItems: 'center',
	gap: 2,
	gridTemplateAreas: {
		xs: `"checks files"
		"checks comment"
		"checks user"
		"checks visibility"`,
		md: '"checks files comment user visibility"',
	},
	gridTemplateColumns: {
		xs: 'auto 1fr',
		md: 'auto 6fr 7fr 3fr auto',
	},
} as const;

type ServiceAgreementTransferFilesProps = {
	unitId: number;
};

export const ServiceAgreementTransferFiles = ({
	unitId,
}: ServiceAgreementTransferFilesProps): React.JSX.Element => {
	const { t } = useTranslation([
		'fileUpload',
		'productIndividual',
		'common',
		'commonButton',
		'component',
		'administration',
		'unitOverview',
	]);

	const { api } = useProductCenterApi();
	const queryClient = useQueryClient();
	const { enqueueSnackbar } = useSnackbar();
	const currentUser = useAppSelector(selectUser);
	const currentCustomerNumber = currentUser.customer?.customerNumber ?? null;

	const fullScreen = useMediaQuery((theme: Theme) =>
		theme.breakpoints.only('xs')
	);

	const [open, setOpen] = useState(false);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [visibilityMap, setVisibilityMap] = useState<Record<number, boolean>>(
		{}
	);

	const {
		data: unitInfo,
		isLoading,
		isError,
	} = useQuery({
		...unitInfoQuery(api, unitId),
		enabled: !!unitId && open,
	});

	const groupedRows = useMemo<FileGroup[]>(
		() =>
			(unitInfo?.allFileSets ?? []).map((set) => ({
				fileSetId: set.id,
				comment: set.comment,
				submittedBy: set.submittedBy,
				isOwn:
					set.submittedByCustomerId ===
					currentCustomerNumber?.toString(),
				files: set.files.map((file) => ({
					rowKey: `${set.id}_${file.id}`,
					fileSetId: set.id,
					fileId: file.id,
					fileName: file.fileName,
				})),
			})),
		[unitInfo, currentCustomerNumber]
	);

	const handleClose = useCallback(() => {
		setOpen(false);
		setSelectedIds(new Set());
		setVisibilityMap({});
	}, []);

	const transferMutation = useMutation({
		mutationFn: (
			payloads: NonNullable<ImportAdditionalFilesToServiceAgreementCommand>[]
		) =>
			Promise.all(
				payloads.map((payload) =>
					api.importAdditionalFilesToServiceAgreement(
						payload.unitId,
						payload
					)
				)
			),

		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: unitKeys.serviceAgreementFiles(unitId),
				}),
				queryClient.invalidateQueries({
					queryKey: unitInfoQuery(api, unitId).queryKey,
				}),
			]);
			enqueueSnackbar(t('administration:upload-pi-sucessful'), {
				variant: 'success',
			});
			handleClose();
		},
	});

	const handleOpen = () => {
		setSelectedIds(new Set());
		setVisibilityMap({});
		setOpen(true);
	};

	const toggleGroup = (fileSetId: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(fileSetId)) {
				next.delete(fileSetId);
			} else {
				next.add(fileSetId);
			}
			return next;
		});
	};

	const handleVisibilityChange = (
		fileSetId: number,
		event: SelectChangeEvent<string>
	) => {
		setVisibilityMap((prev) => ({
			...prev,
			[fileSetId]: event.target.value === 'public',
		}));
	};

	const resolveVisibility = (fileSetId: number): boolean =>
		fileSetId in visibilityMap ? visibilityMap[fileSetId] : false;

	const handleTransfer = () => {
		const selectedGroups = groupedRows.filter((group) =>
			selectedIds.has(group.fileSetId)
		);
		if (selectedGroups.length === 0) return;
		const payloads: NonNullable<ImportAdditionalFilesToServiceAgreementCommand>[] =
			selectedGroups.map((group) => ({
				unitId,
				fileSetIds: [group.fileSetId],
				comment: group.comment,
				isPublic: resolveVisibility(group.fileSetId),
			}));
		transferMutation.mutate(payloads);
	};

	const selectedCount = selectedIds.size;

	let content: React.ReactNode;

	if (!isError && !isLoading && groupedRows.length > 0) {
		content = (
			<Box>
				<Typography variant="subtitle1" sx={{ mb: 2 }}>
					{t('unitOverview:transfer-files-helper-text')}
				</Typography>
				{groupedRows.map((group, index) => (
					<Fragment key={group.fileSetId}>
						{index > 0 && <Divider sx={{ my: 2 }} />}
						<Box sx={groupGridSx}>
							<Box>
								<Checkbox
									checked={selectedIds.has(group.fileSetId)}
									onChange={() =>
										toggleGroup(group.fileSetId)
									}
									size="small"
								/>
							</Box>
							<Stack
								direction="column"
								spacing={2}
								sx={{ gridArea: 'files', alignSelf: 'start' }}>
								{group.files.map((row) => (
									<FileDisplayBase
										key={row.rowKey}
										name={row.fileName}
										url={getUrlFileSetDownload(
											row.fileSetId,
											row.fileId
										)}
										type={getFileType(row.fileName) ?? ''}
									/>
								))}
							</Stack>

							<Box
								sx={{
									color: 'text.secondary',
								}}>
								{!group.comment?.trim() && (
									<ChatBubbleOutline
										color="disabled"
										sx={{ mr: 1, verticalAlign: 'bottom' }}
									/>
								)}
								<Typography component="span">
									{group.comment ?? ''}
								</Typography>
							</Box>

							<Typography
								sx={{
									color: 'text.secondary',
								}}>
								<UserIcon
									color="primary"
									opacity={0.4}
									sx={{ mr: 1, verticalAlign: 'bottom' }}
								/>
								{group.submittedBy}
							</Typography>

							<Box>
								{group.isOwn && (
									<FormControl
										size="small"
										fullWidth
										sx={{ minWidth: 140 }}>
										<Select
											disableUnderline
											variant="standard"
											value={
												resolveVisibility(
													group.fileSetId
												)
													? 'public'
													: 'internal'
											}
											onChange={(e) =>
												handleVisibilityChange(
													group.fileSetId,
													e
												)
											}>
											<MenuItem value="internal">
												<SystemIcon
													icon="view-off"
													size={30}
													style={{
														marginRight: 6,
														verticalAlign: 'middle',
													}}
												/>
												{t(
													'productIndividual:document-visibility-label-internal'
												)}
											</MenuItem>
											<MenuItem value="public">
												<SystemIcon
													icon="view"
													size={30}
													style={{
														marginRight: 6,
														verticalAlign: 'middle',
													}}
												/>
												{t(
													'productIndividual:document-visibility-label-public'
												)}
											</MenuItem>
										</Select>
									</FormControl>
								)}
							</Box>
						</Box>
					</Fragment>
				))}
			</Box>
		);
	}

	return (
		<>
			<Button
				startIcon={<SwapHoriz />}
				color="secondary"
				disabled={!unitId}
				onClick={handleOpen}>
				{t('unitOverview:transfer-file')}
			</Button>

			<Dialog
				maxWidth="lg"
				fullWidth
				open={open}
				onClose={handleClose}
				fullScreen={fullScreen}>
				<DialogTitle>
					{t('unitOverview:select-files-title')}
					{selectedCount > 0 && ` (${selectedCount})`}
				</DialogTitle>
				<DialogContent>
					<Typography
						variant="body1"
						sx={{
							color: 'text.secondary',
							mb: 3,
						}}>
						{t('unitOverview:transfer-files-subtitle')}
					</Typography>
					<TransferFilesStatusContent
						isError={isError}
						isLoading={isLoading}
						isEmpty={groupedRows.length === 0}>
						{content}
					</TransferFilesStatusContent>
				</DialogContent>
				<TransferFilesDialogActions
					onClose={handleClose}
					onTransfer={handleTransfer}
					isPending={transferMutation.isPending}
					selectedCount={selectedCount}
				/>
			</Dialog>
		</>
	);
};
